import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import i18n from 'i18next';
import { enqueueSnackbar } from 'notistack';
import { closePermissionsView } from 'scenes/Permissions/store/actions/permissions-view.actions';
import { clearGalleryData } from 'scenes/ProjectGallery/store/actions/projectGallery.actions';
import { loadMediaFilePermissionsByUserAndProject } from 'scenes/ProjectGallery/store/thunks/loadMediaFilePermissionsByUserAndProject';
import { closeProjectResourcesView } from 'scenes/ProjectResources/store/actions/project-resources.actions';
import { onSearchBusinessClick } from 'scenes/Settings/store/thunks/onSearchBusinessClick.thunk';
import { closeShortTermProjectsView } from 'scenes/ShortTermProjects/store/actions/shortTermProjects.actions';
import { loadInitialCalendarData } from 'scenes/WorkDemo/store/thunks/loadInitialCalendarData.thunk';
import { closeProjectOrganizationView } from 'store/organizationCharts/actions/organizationChart.actions';
import { deleteBusinessOrganizationFromProject } from 'store/organizationCharts/thunks/deleteBusinessOrganizationFromProject.thunk';
import { loadProjectOrganizationChartResources } from 'store/organizationCharts/thunks/loadProjectOrganizationChartResources.thunk';
import { addEmployerForUser } from 'store/projects/thunks/addEmployerForUser.thunk';
import { createProject } from 'store/projects/thunks/createProject.thunk';
import { loadUserProjectsAndResources } from 'store/projects/thunks/project.thunks';
import { userJoinProject } from 'store/projects/thunks/userJoinProject.thunk';
import { setLoadedResourcesProjectId } from 'store/records/actions/records.actions';
import { editRecord } from 'store/records/thunks/editRecord';
import { editRecords } from 'store/records/thunks/editRecords.thunk';
import { loadProjectRecordsFromToday } from 'store/records/thunks/loadProjectRecordsFromToday.thunk';
import { createOrUpdateUser } from 'store/user/thunks/createOrUpdateUser.thunk';
import { setUpdateUserEmployerBusinessIdResources } from 'store/userProfile/thunks/setUpdateUserEmployerBusinessIdResources.thunk';
import { setUpdateUserEmployerBusinessIdResourcesFromOrientationForm } from 'store/userProfile/thunks/setUpdateUserEmployerBusinessIdResourcesFromOrientationForm.thunk';
import {
  loggedInUserResourcesAdapter,
  resourcesAdapter,
  resourcesToMakeAdminOfAdapter,
  resourcesWithTaxNumberStatusAdapter,
} from './resourceSliceSelectors';
import { joinExistingEmployeeToProject } from './thunks/joinExistingEmployeeToProject.thunk';
import { leaveProject } from './thunks/leaveProject.thunk';
import { loadAllResourcesWithProjectBusinessId } from './thunks/loadAllResourcesWithProjectBusinessId';
import { loadResourcesAndResourceEmployersOfProject } from './thunks/loadResourcesAndResourceEmployersOfProject';
import { loadResourcesDataOfProjectSpecificFeature } from './thunks/loadResourcesDataOfProjectSpecificFeature';
import { loadResourcesOfProject } from './thunks/loadResourcesOfProject';
import { loadResourcesOfProjectWithEmployerData } from './thunks/loadResourcesOfProjectWithEmployerData';
import { loadResourcesThatHaveJoinedProjectsOfBusiness } from './thunks/loadResourcesThatHaveJoinedProjectsOfBusiness.thunk';
import { loadUserResourcesForProject } from './thunks/loadUserResourcesForProject.thunk';
import { removeResourceFromProject } from './thunks/removeResourceFromProject.thunk';
import { setUpdateResourcesFromSettingProfile } from './thunks/setUpdateResourcesFromSettingProfile.thunk';

const initialState = {
  loadedResourcesProjectIds: [],
  resources: resourcesAdapter.getInitialState(),
  resourcesWithTaxNumberStatus:
    resourcesWithTaxNumberStatusAdapter.getInitialState(),
  loggedInUserResources: loggedInUserResourcesAdapter.getInitialState(),
  resourcesToMakeAdminOf: resourcesToMakeAdminOfAdapter.getInitialState(),
  deleteResourcePending: false,
  loading: false,
  loadingResources: false,
  loadingResourcesToMakeAdminOf: false,
  deleteResourceError: '',
  isJoiningEmployeeToProject: false,
  allResourcesWithProjectBusinessId: [],
  loadingAllResourcesWithProjectBusinessId: false,
};

const resourcesSlice = createSlice({
  name: 'resources',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadResourcesOfProject.pending, (state) => {
        state.loading = true;
      })

      .addCase(loadResourcesOfProject.fulfilled, (state, action) => {
        state.loading = false;
        resourcesAdapter.setMany(state.resources, action.payload);
      })
      .addCase(loadResourcesOfProject.rejected, (state, action) => ({
        ...state,
        loading: false,
        error: action.error,
      }))
      .addCase(loadResourcesOfProjectWithEmployerData.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        loadResourcesOfProjectWithEmployerData.fulfilled,
        (state, action) => {
          state.loading = false;
          resourcesAdapter.setMany(state.resources, action.payload.resources);
          resourcesWithTaxNumberStatusAdapter.setMany(
            state.resourcesWithTaxNumberStatus,
            action.payload.resourcesWithTaxNumberStatus
          );
        }
      )
      .addCase(
        loadResourcesOfProjectWithEmployerData.rejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error;
        }
      )
      .addCase(
        loadResourcesDataOfProjectSpecificFeature.fulfilled,
        (state, action) => {
          state.loading = false;
          resourcesAdapter.setMany(state.resources, action.payload.resources);
        }
      )
      .addCase(setUpdateResourcesFromSettingProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        setUpdateResourcesFromSettingProfile.fulfilled,
        (state, action) => {
          state.loading = false;
          resourcesAdapter.updateMany(state.resources, action.payload);
          loggedInUserResourcesAdapter.setMany(
            state.loggedInUserResources,
            action.payload
          );
        }
      )
      .addCase(setUpdateResourcesFromSettingProfile.rejected, (state) => {
        state.loading = false;
      })
      .addCase(setUpdateUserEmployerBusinessIdResources.pending, (state) => {
        // state.loading = true;
      })
      .addCase(
        setUpdateUserEmployerBusinessIdResources.fulfilled,
        (state, action) => {
          state.loading = false;
          const updatedResource = action.payload;

          const { taxNumberStatus, taxNumberCheckedAt, ...rest } =
            updatedResource;

          // Upsert the resource returned by the API
          resourcesAdapter.upsertOne(state.resources, rest);
          resourcesWithTaxNumberStatusAdapter.upsertOne(
            state.resourcesWithTaxNumberStatus,
            updatedResource
          );
        }
      )
      .addCase(setUpdateUserEmployerBusinessIdResources.rejected, (state) => {
        state.loading = false;
      })
      .addCase(
        setUpdateUserEmployerBusinessIdResourcesFromOrientationForm.pending,
        (state) => {
          // state.loading = true;
        }
      )
      .addCase(
        setUpdateUserEmployerBusinessIdResourcesFromOrientationForm.fulfilled,
        (state, action) => {
          state.loading = false;
          resourcesAdapter.updateMany(state.resources, action.payload);
        }
      )
      .addCase(
        setUpdateUserEmployerBusinessIdResourcesFromOrientationForm.rejected,
        (state) => {
          state.loading = false;
        }
      )
      .addCase(setLoadedResourcesProjectId, (state, action) => {
        state.loadedResourcesProjectIds = [
          ...state.loadedResourcesProjectIds,
        ].includes(action.payload)
          ? [...state.loadedResourcesProjectIds]
          : [...state.loadedResourcesProjectIds, action.payload];
      })
      .addCase(loadUserResourcesForProject.pending, (state) => {
        state.loading = true;
        state.isUserProjectResourcesLoaded = false;
      })
      .addCase(loadUserResourcesForProject.fulfilled, (state, action) => {
        state.loading = false;
        const userResourcesForProject = action.payload.filter(
          (resource) => resource && resource.id
        );

        resourcesAdapter.setMany(state.resources, userResourcesForProject);
        loggedInUserResourcesAdapter.setMany(
          state.loggedInUserResources,
          userResourcesForProject
        );

        state.isUserProjectResourcesLoaded = true;
      })
      .addCase(loadUserResourcesForProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
        state.isUserProjectResourcesLoaded = true;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        resourcesAdapter.setOne(state.resources, action.payload.newResource);
        loggedInUserResourcesAdapter.setOne(
          state.loggedInUserResources,
          action.payload.newResource
        );
      })
      .addCase(loadInitialCalendarData.fulfilled, (state, action) => {
        resourcesAdapter.setMany(state.resources, action.payload.resources);
      })

      .addCase(loadUserProjectsAndResources.pending, (state, action) => {
        state.loadingResources = true;
      })
      .addCase(loadUserProjectsAndResources.fulfilled, (state, action) => {
        state.loadingResources = false;
        resourcesAdapter.setMany(state.resources, action.payload.resources);
        loggedInUserResourcesAdapter.setMany(
          state.loggedInUserResources,
          action.payload.resources
        );
      })
      .addCase(loadUserProjectsAndResources.rejected, (state) => {
        state.loadingResources = false;
      })
      .addCase(onSearchBusinessClick.pending, (state) => {
        state.loadingResourcesToMakeAdminOf = true;
        resourcesToMakeAdminOfAdapter.removeAll(state.resourcesToMakeAdminOf);
      })
      .addCase(onSearchBusinessClick.rejected, (state) => {
        state.loadingResourcesToMakeAdminOf = false;
      })
      .addCase(onSearchBusinessClick.fulfilled, (state, action) => {
        state.loadingResourcesToMakeAdminOf = false;
        resourcesToMakeAdminOfAdapter.setMany(
          state.resourcesToMakeAdminOf,
          action.payload.resources
        );
      })
      .addCase(loadProjectRecordsFromToday.fulfilled, (state, action) => {
        state.resources = resourcesAdapter.setMany(
          state.resources,
          action.payload.projectRecordsResources
        );
      })
      .addCase(editRecords.fulfilled, (state, action) => {
        state.resources = resourcesAdapter.setMany(
          state.resources,
          action.payload.resources
        );
      })
      .addCase(addEmployerForUser.fulfilled, (state, action) => {
        state.resources = resourcesAdapter.setOne(
          state.resources,
          action.payload.newResource
        );
        loggedInUserResourcesAdapter.setOne(
          state.loggedInUserResources,
          action.payload.newResource
        );
      })
      .addCase(userJoinProject.fulfilled, (state, action) => {
        resourcesAdapter.setMany(state.resources, action.payload.resources);
        loggedInUserResourcesAdapter.setMany(
          state.loggedInUserResources,
          action.payload.resources
        );
      })
      .addCase(leaveProject.fulfilled, (state, action) => {
        resourcesAdapter.removeOne(state.resources, action.payload.id);
        loggedInUserResourcesAdapter.removeOne(
          state.loggedInUserResources,
          action.payload.id
        );

        state.deleteResourcePending = false;
      })
      .addCase(leaveProject.pending, (state, action) => {
        state.deleteResourcePending = true;
      })
      .addCase(leaveProject.rejected, (state, action) => {
        state.deleteResourcePending = false;
        state.deleteResourceError = action.payload.message;
      })
      .addCase(editRecord.fulfilled, (state, action) => {
        if (action.payload.resource) {
          state.resources = resourcesAdapter.setOne(
            state.resources,
            action.payload.resource
          );
        }
      })
      .addCase(
        loadResourcesAndResourceEmployersOfProject.fulfilled,
        (state, action) => {
          if (action.payload.resources) {
            const resources = action.payload.resources.filter(
              (resource) => resource.id
            );

            state.resources = resourcesAdapter.setMany(
              state.resources,
              resources
            );
          }
        }
      )
      .addCase(
        loadMediaFilePermissionsByUserAndProject.fulfilled,
        (state, action) => {
          const resources = action.payload.resources;
          resourcesAdapter.setMany(state.resources, resources);
        }
      )
      .addCase(createOrUpdateUser.fulfilled, (state, action) => {
        if (action.payload.resource) {
          resourcesAdapter.setOne(state.resources, action.payload.resource);
        }
      })
      .addCase(joinExistingEmployeeToProject.pending, (state, action) => {
        state.isJoiningEmployeeToProject = true;
      })
      .addCase(joinExistingEmployeeToProject.fulfilled, (state, action) => {
        if (action.payload.resource) {
          resourcesAdapter.setOne(state.resources, action.payload.resource);
        }
        state.isJoiningEmployeeToProject = false;
      })
      .addCase(joinExistingEmployeeToProject.rejected, (state, action) => {
        state.isJoiningEmployeeToProject = false;
      })
      .addCase(loadAllResourcesWithProjectBusinessId.pending, (state) => {
        state.loadingAllResourcesWithProjectBusinessId = true;
      })
      .addCase(
        loadAllResourcesWithProjectBusinessId.fulfilled,
        (state, action) => {
          state.loadingAllResourcesWithProjectBusinessId = false;
          state.allResourcesWithProjectBusinessId = action.payload;
        }
      )
      .addCase(
        loadAllResourcesWithProjectBusinessId.rejected,
        (state, action) => {
          state.loadingAllResourcesWithProjectBusinessId = false;
          state.error = action.error;
        }
      )
      .addCase(
        loadProjectOrganizationChartResources.pending,
        (state, action) => {
          state.loading = true;
        }
      )
      .addCase(
        loadProjectOrganizationChartResources.fulfilled,
        (state, action) => {
          resourcesAdapter.setMany(state.resources, action.payload);
          state.loading = false;
        }
      )
      .addCase(
        loadProjectOrganizationChartResources.rejected,
        (state, action) => {
          state.loading = false;
        }
      )
      .addCase(
        deleteBusinessOrganizationFromProject.fulfilled,
        (state, action) => {
          const { deletedResources } = action.payload;

          resourcesAdapter.setMany(state.resources, deletedResources);
        }
      )
      .addCase(
        loadResourcesThatHaveJoinedProjectsOfBusiness.fulfilled,
        (state, action) => {
          resourcesAdapter.upsertMany(state.resources, action.payload);
        }
      )
      .addCase(removeResourceFromProject.pending, (state, action) => {
        state.deleteResourcePending = true;
      })
      .addCase(removeResourceFromProject.fulfilled, (state, action) => {
        resourcesAdapter.upsertOne(state.resources, action.payload);
        state.deleteResourcePending = false;
        enqueueSnackbar(i18n.t('organization_chart.remove_employee_success'), {
          variant: 'success',
        });
      })
      .addCase(removeResourceFromProject.rejected, (state, action) => {
        state.deleteResourcePending = false;
        enqueueSnackbar(i18n.t('organization_chart.remove_employee_error'), {
          variant: 'error',
        });
      })
      .addMatcher(
        isAnyOf(
          clearGalleryData,
          closePermissionsView,
          closeProjectResourcesView,
          closeShortTermProjectsView,
          closeProjectOrganizationView
        ),
        (state, action) => {
          state.resources = resourcesAdapter.getInitialState();
        }
      );
  },
});

export default resourcesSlice.reducer;
